import { Trash2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useAgentCleanupMutations } from '../hooks/useAgentMutations'
import { ApiKeysManager } from '../components/ApiKeysManager'
import { teamService, engineService, settingsService, agentService, userService, type User } from '../services/api'
import { Secrets } from './Secrets'
import { SkillCatalogPanel } from '../components/SkillCatalogPanel'
import { PersonalAccessTokenCard } from '../components/PersonalAccessTokenCard'
import { showErrorAlert, showSuccessAlert, logError } from '../utils/errors'
import { useAuthStore } from '../stores/authStore'

export function Settings({ panel = 'admin' }: { panel?: 'profil' | 'secrets' | 'admin' }) {
  const { cleanInactive, cleanDuplicates, cleanAllInstances, cleanAllModels, cleanAll } = useAgentCleanupMutations()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuthStore()
  
  // * Check if current user is admin
  const isAdmin = currentUser?.role === 'admin'
  
  // * Fetch all users (admin only)
  const { data: allUsers, refetch: refetchUsers } = useQuery(
    'allUsers',
    userService.getAllUsers,
    {
      enabled: isAdmin
    }
  )
  
  // * Fetch team templates and instances
  const { data: teamTemplates } = useQuery('teamTemplates', teamService.getAllTeamTemplates)
  const { data: teamInstances } = useQuery('teamInstances', teamService.getAllTeamInstances)
  
  // * Fetch agents for counting
  const { data: allAgents } = useQuery('agents', agentService.getAllAgents)
  const { data: activeAgents } = useQuery('activeAgents', agentService.getActiveAgents)
  
  // * active_status IS NULL = modèle (jamais enregistré).
  // * active_status = 'inactive' = instance hibernée, déjà dans activeAgents.
  // * Les deux compteurs étaient le même filtre (tout ce qui n'est pas actif),
  // * donc « Nettoyer les inactifs » affichait et supprimait les modèles.
  const inactiveCount = activeAgents?.filter(agent => agent.activeStatus === 'inactive').length || 0
  
  const duplicateCount = allAgents 
    ? (() => {
        const containerUuids = new Map<string, number>()
        allAgents.forEach(agent => {
          const uuid = (agent.config as any)?.containerUuid
          if (uuid) {
            containerUuids.set(uuid, (containerUuids.get(uuid) || 0) + 1)
          }
        })
        // * Count agents that have duplicates (more than 1 agent with same containerUuid)
        let duplicates = 0
        containerUuids.forEach((count, uuid) => {
          if (count > 1) {
            // * Keep the most recent, delete the rest
            duplicates += count - 1
          }
        })
        return duplicates
      })()
    : 0
  
  const instancesCount = activeAgents?.length || 0

  const modelsCount = allAgents?.filter(agent => !agent.activeStatus).length || 0
  
  const totalAgentsCount = allAgents?.length || 0
  
  // * Fetch engines and default engine
  const { data: engines } = useQuery('engines', engineService.listEngines)
  const { data: defaultEngine, refetch: refetchDefaultEngine } = useQuery(
    'defaultEngine',
    () => settingsService.getSetting('default_agent_engine'),
    {
      initialData: 'agent-python-full' // * Default fallback
    }
  )
  
  // * Mutation to update default engine
  const updateDefaultEngineMutation = useMutation(
    (engine: string) => settingsService.setSetting('default_agent_engine', engine, 'Moteur d\'agent par défaut pour les déploiements'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('defaultEngine')
        refetchDefaultEngine()
        showSuccessAlert('Moteur par défaut mis à jour avec succès')
      },
      onError: (error) => {
        logError('Mise à jour moteur par défaut', error)
        showErrorAlert(error, 'Erreur lors de la mise à jour du moteur par défaut')
      }
    }
  )
  
  // * Mutation to delete all team templates
  const deleteAllTeamTemplates = useMutation(
    async () => {
      if (!teamTemplates || teamTemplates.length === 0) {
        return { deletedCount: 0 }
      }
      let deletedCount = 0
      for (const template of teamTemplates) {
        try {
          await teamService.deleteTeamTemplate(template.id)
          deletedCount++
        } catch (error) {
          logError(`Erreur lors de la suppression du modèle d'équipe ${template.id}`, error)
        }
      }
      return { deletedCount }
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('teamTemplates')
        queryClient.invalidateQueries('teamInstances')
        showSuccessAlert(`${data.deletedCount} modèle(s) d'équipe supprimé(s) avec succès`)
      },
      onError: (error) => {
        logError('Suppression tous les modèles d\'équipe', error)
        showErrorAlert(error, 'Erreur lors de la suppression des modèles d\'équipe')
      }
    }
  )
  
  // * Mutation to delete all team instances
  const deleteAllTeamInstances = useMutation(
    async () => {
      if (!teamInstances || teamInstances.length === 0) {
        return { deletedCount: 0 }
      }
      let deletedCount = 0
      for (const instance of teamInstances) {
        try {
          await teamService.deleteTeamInstance(instance.id)
          deletedCount++
        } catch (error) {
          logError(`Erreur lors de la suppression de l'instance d'équipe ${instance.id}`, error)
        }
      }
      return { deletedCount }
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('teamInstances')
        queryClient.invalidateQueries('activeAgents')
        showSuccessAlert(`${data.deletedCount} instance(s) d'équipe supprimée(s) avec succès`)
      },
      onError: (error) => {
        logError('Suppression toutes les instances d\'équipe', error)
        showErrorAlert(error, 'Erreur lors de la suppression des instances d\'équipe')
      }
    }
  )

  const handleCleanInactive = async () => {
    if (window.confirm('Supprimer les instances hibernées (statut inactif) et leurs conteneurs ? Les modèles d\'agents ne sont pas touchés. Cette action est irréversible.')) {
      await cleanInactive.mutateAsync()
    }
  }

  const handleCleanDuplicates = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer tous les agents en double (même containerUuid) ? Cette action est irréversible. Seul l\'agent le plus récent sera conservé pour chaque containerUuid.')) {
      await cleanDuplicates.mutateAsync()
    }
  }

  const handleCleanAllInstances = async () => {
    if (window.confirm('⚠️ ATTENTION : Supprimer toutes les instances enregistrées, leurs conteneurs, et les conteneurs qui n\'ont jamais réussi à s\'enregistrer ? Les modèles d\'agents sont conservés. Cette action est irréversible.')) {
      await cleanAllInstances.mutateAsync()
    }
  }

  const handleCleanAllModels = async () => {
    if (window.confirm('⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer TOUS les modèles d\'agents ? Cette action est irréversible et supprimera uniquement les agents sans instance (modèles non déployés), pas les instances actives.')) {
      await cleanAllModels.mutateAsync()
    }
  }

  const handleCleanAllAgents = async () => {
    if (window.confirm('⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer TOUS les agents (instances ET modèles) ? Cette action est irréversible et supprimera tous les agents de la base de données.')) {
      if (window.confirm('⚠️ DERNIÈRE CONFIRMATION : Vous êtes sur le point de supprimer TOUS les agents. Cette action ne peut pas être annulée. Confirmez-vous ?')) {
        await cleanAll.mutateAsync()
      }
    }
  }

  const handleDeleteAllTeamTemplates = async () => {
    const count = teamTemplates?.length || 0
    if (count === 0) {
      alert('Aucun modèle d\'équipe à supprimer')
      return
    }
    if (window.confirm(`⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer TOUS les modèles d'équipe (${count}) ? Cette action est irréversible et supprimera également tous les rôles associés.`)) {
      await deleteAllTeamTemplates.mutateAsync()
    }
  }

  const handleDeleteAllTeamInstances = async () => {
    const count = teamInstances?.length || 0
    if (count === 0) {
      alert('Aucune instance d\'équipe à supprimer')
      return
    }
    if (window.confirm(`⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer TOUTES les instances d'équipe (${count}) ? Cette action est irréversible. Les agents assignés à ces équipes seront retirés de leurs équipes.`)) {
      await deleteAllTeamInstances.mutateAsync()
    }
  }

  // * Mutation to update user role
  const updateUserRoleMutation = useMutation(
    ({ userId, role }: { userId: string; role: 'guest' | 'user' | 'admin' }) => 
      userService.updateUserRole(userId, role),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('allUsers')
        refetchUsers()
        showSuccessAlert('Rôle utilisateur mis à jour avec succès')
      },
      onError: (error) => {
        logError('Mise à jour rôle utilisateur', error)
        showErrorAlert(error, 'Erreur lors de la mise à jour du rôle utilisateur')
      }
    }
  )

  const handleUpdateUserRole = async (userId: string, newRole: 'guest' | 'user' | 'admin') => {
    if (userId === currentUser?.id && newRole !== 'admin') {
      alert('Vous ne pouvez pas retirer vos propres droits administrateur')
      return
    }
    await updateUserRoleMutation.mutateAsync({ userId, role: newRole })
  }

  const getRoleLabel = (role: 'guest' | 'user' | 'admin'): string => {
    switch (role) {
      case 'guest':
        return 'Invité'
      case 'user':
        return 'Utilisateur'
      case 'admin':
        return 'Administrateur'
      default:
        return role
    }
  }

  const getRoleDescription = (role: 'guest' | 'user' | 'admin'): string => {
    switch (role) {
      case 'guest':
        return 'Navigation possible, aucun droit de création, peut voir les modèles et instances publiques'
      case 'user':
        return 'Fonctionnement normal, création et surveillance de ses modèles et instances'
      case 'admin':
        return 'Peut voir tous les agents et a accès à tous les boutons de paramétrages'
      default:
        return ''
    }
  }

  if (panel === 'profil') {
    const roleLabel = currentUser?.role === 'admin'
      ? 'Administrateur'
      : currentUser?.role === 'guest'
        ? 'Invité'
        : 'Utilisateur'
    return (
      <div className="page-content relative min-h-screen">
        <div className="background-image">
          <img src="/logo.jpg" alt="" />
        </div>
        <div className="content-wrapper">
          <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Profil</h1>
          <div className="card mt-6">
            <div className="px-6 py-4 space-y-3">
              <p className="text-primary"><span className="text-secondary">Nom</span> — {currentUser?.name || currentUser?.username || 'Utilisateur'}</p>
              <p className="text-primary"><span className="text-secondary">Identifiant</span> — {currentUser?.username || '—'}</p>
              <p className="text-primary"><span className="text-secondary">Courriel</span> — {currentUser?.email || '—'}</p>
              <p className="text-primary"><span className="text-secondary">Rôle</span> — {roleLabel}</p>
            </div>
          </div>
          <PersonalAccessTokenCard />
        </div>
      </div>
    )
  }

  if (panel === 'secrets') {
    return (
      <div className="page-content relative min-h-screen">
        <div className="background-image">
          <img src="/logo.jpg" alt="" />
        </div>
        <div className="content-wrapper space-y-6">
          <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Secrets</h1>
          <ApiKeysManager />
          <Secrets />
        </div>
      </div>
    )
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
    <div className="space-y-6">
      <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Admin</h1>

      {isAdmin && <SkillCatalogPanel />}

      {/* Configuration du moteur d'agent par défaut */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-primary">Moteur d'agent par défaut</h2>
          <p className="text-sm text-secondary mt-1">Choisissez le moteur d'agent à utiliser pour tous les déploiements</p>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-2">Moteur d'agent :</label>
              <select id="settings-default-engine" name="settings-default-engine"
                value={defaultEngine || 'agent-python-full'}
                onChange={(e) => updateDefaultEngineMutation.mutate(e.target.value)}
                disabled={updateDefaultEngineMutation.isLoading || !engines || engines.length === 0}
                className="form-input w-full"
              >
                {engines && engines.length > 0 ? (
                  engines.map(engine => (
                    <option key={engine.name} value={engine.name}>
                      {engine.name}
                    </option>
                  ))
                ) : (
                  <option value="agent-python-full">agent-python-full (par défaut)</option>
                )}
              </select>
              {updateDefaultEngineMutation.isLoading && (
                <p className="text-xs text-secondary mt-1">Mise à jour en cours...</p>
              )}
              {engines && engines.length === 0 && (
                <p className="text-xs text-warning mt-1">
                  Aucun moteur trouvé dans /app/AgentTeamForge/ATF_agents_engines/build/
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gestion des instances d'agents actifs */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-primary">Gestion des agents</h2>
          <p className="text-sm text-secondary mt-1">Une instance est un conteneur enregistré. Un modèle n'a pas de conteneur. Nettoyer les inactifs ne supprime pas les modèles.</p>
        </div>
        <div className="px-6 py-4 space-y-3">
          {/* Nettoyage des instances */}
          <div>
            <h3 className="text-sm font-semibold text-primary mb-2">Instances d'agents déployées</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <button
                onClick={handleCleanInactive}
                disabled={cleanInactive.isLoading}
                className="button button--secondary flex items-center gap-2 justify-center text-sm py-2 px-3"
                title="Supprimer les instances hibernées. Les modèles restent."
              >
                <Trash2 className={`w-4 h-4 ${cleanInactive.isLoading ? 'animate-spin' : ''}`} />
                <span>Nettoyer les inactifs ({inactiveCount})</span>
              </button>

              <button
                onClick={handleCleanDuplicates}
                disabled={cleanDuplicates.isLoading}
                className="button button--secondary flex items-center gap-2 justify-center text-sm py-2 px-3 bg-orange-500 hover:bg-orange-600"
                title="Supprimer les agents en double"
              >
                <Trash2 className={`w-4 h-4 ${cleanDuplicates.isLoading ? 'animate-spin' : ''}`} />
                <span>Nettoyer les doublons ({duplicateCount})</span>
              </button>

              <button
                onClick={handleCleanAllInstances}
                disabled={cleanAllInstances.isLoading}
                className="button button--secondary flex items-center gap-2 justify-center text-sm py-2 px-3 bg-red-500 hover:bg-red-600"
                title="Supprimer les instances enregistrées et les conteneurs jamais enregistrés. Les modèles restent."
              >
                <Trash2 className={`w-4 h-4 ${cleanAllInstances.isLoading ? 'animate-spin' : ''}`} />
                <span>Supprimer toutes les instances ({instancesCount})</span>
              </button>
            </div>
          </div>

          {/* Nettoyage des modèles */}
          <div className="border-t border-gray-200 pt-3">
            <h3 className="text-sm font-semibold text-primary mb-2">Modèles d'agents</h3>
            <button
              onClick={handleCleanAllModels}
              disabled={cleanAllModels.isLoading}
              className="button button--secondary flex items-center gap-2 bg-red-500 hover:bg-red-600 text-sm py-2 px-3"
              title="Supprimer tous les modèles d'agents"
            >
              <Trash2 className={`w-4 h-4 ${cleanAllModels.isLoading ? 'animate-spin' : ''}`} />
              <span>Supprimer tous les modèles ({modelsCount})</span>
            </button>
          </div>

          {/* Suppression totale */}
          <div className="border-t-2 border-red-300 pt-3">
            <h3 className="text-sm font-semibold text-red-600 mb-2">⚠️ Suppression totale</h3>
            <button
              onClick={handleCleanAllAgents}
              disabled={cleanAll.isLoading}
              className="button button--secondary flex items-center gap-2 bg-red-600 hover:bg-red-700 text-sm py-2 px-3 w-full"
              title="Supprimer tous les agents (instances + modèles)"
            >
              <Trash2 className={`w-4 h-4 ${cleanAll.isLoading ? 'animate-spin' : ''}`} />
              <span>Supprimer TOUT (instances + modèles) ({totalAgentsCount})</span>
            </button>
          </div>
        </div>
      </div>

      {/* Gestion des équipes */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-primary">Gestion des équipes</h2>
          <p className="text-sm text-secondary mt-1">Actions de maintenance pour nettoyer les modèles et instances d'équipes</p>
        </div>
        <div className="px-6 py-4 space-y-3">
          {/* Suppression des modèles d'équipe */}
          <div>
            <h3 className="text-sm font-semibold text-primary mb-2">Modèles d'équipes</h3>
            <button
              onClick={handleDeleteAllTeamTemplates}
              disabled={deleteAllTeamTemplates.isLoading}
              className="button button--secondary flex items-center gap-2 bg-red-500 hover:bg-red-600 text-sm py-2 px-3"
              title="Supprimer tous les modèles d'équipe"
            >
              <Trash2 className={`w-4 h-4 ${deleteAllTeamTemplates.isLoading ? 'animate-spin' : ''}`} />
              <span>Supprimer tous les modèles d'équipe ({teamTemplates?.length || 0})</span>
            </button>
          </div>

          {/* Suppression des instances d'équipe */}
          <div className="border-t border-gray-200 pt-3">
            <h3 className="text-sm font-semibold text-primary mb-2">Instances d'équipes</h3>
            <button
              onClick={handleDeleteAllTeamInstances}
              disabled={deleteAllTeamInstances.isLoading}
              className="button button--secondary flex items-center gap-2 bg-red-500 hover:bg-red-600 text-sm py-2 px-3"
              title="Supprimer toutes les instances d'équipe"
            >
              <Trash2 className={`w-4 h-4 ${deleteAllTeamInstances.isLoading ? 'animate-spin' : ''}`} />
              <span>Supprimer toutes les instances d'équipe ({teamInstances?.length || 0})</span>
            </button>
          </div>
        </div>
      </div>

      {/* Gestion des utilisateurs (admin only) */}
      {isAdmin && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-primary">Gestion des utilisateurs</h2>
            <p className="text-sm text-secondary mt-1">Gérer les droits d'accès globaux des utilisateurs sur ATF</p>
          </div>
          <div className="px-6 py-4">
            {allUsers && allUsers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-primary">Nom</th>
                      <th className="text-left py-2 px-3 font-semibold text-primary">Email</th>
                      <th className="text-left py-2 px-3 font-semibold text-primary">Username</th>
                      <th className="text-left py-2 px-3 font-semibold text-primary">Rôle actuel</th>
                      <th className="text-left py-2 px-3 font-semibold text-primary">Changer le rôle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((user) => (
                      <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-3">{user.name}</td>
                        <td className="py-3 px-3">{user.email}</td>
                        <td className="py-3 px-3">{user.username}</td>
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                            user.role === 'user' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getRoleLabel(user.role)}
                          </span>
                          <p className="text-xs text-secondary mt-1">{getRoleDescription(user.role)}</p>
                        </td>
                        <td className="py-3 px-3">
                          <select id={`user-role-${user.id}`} name="user-role"
                            value={user.role}
                            onChange={(e) => handleUpdateUserRole(user.id, e.target.value as 'guest' | 'user' | 'admin')}
                            disabled={updateUserRoleMutation.isLoading || (user.id === currentUser?.id && user.role === 'admin')}
                            className="form-input text-sm"
                            title={user.id === currentUser?.id && user.role === 'admin' ? 'Vous ne pouvez pas retirer vos propres droits administrateur' : ''}
                          >
                            <option value="guest">Invité</option>
                            <option value="user">Utilisateur</option>
                            <option value="admin">Administrateur</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-secondary text-sm">Aucun utilisateur trouvé</p>
            )}
          </div>
        </div>
      )}
    </div>
      </div>
    </div>
  )
}
